/**
 * Impact resolution — the ORDER OF OPERATIONS for what a shot does when it
 * lands, as one pure function. Previously this lived inline in
 * Game.handleImpact (main.ts) interleaved with mesh, audio and DOM updates,
 * with a second partial copy in Game.detonateEarly.
 *
 *   crater/mound (or none) → terrain drop → blast damage → deaths (drops,
 *   respawn cooldown) → knockbacks → bot intel → shooter stats → structure
 *   destruction + fines → hazard destruction (+ crystal shatter, blast then
 *   drop-only) → flags/cans → specials (EMP / tether / fire) → flora
 *
 * Returns the new state plus a flat description of everything that happened,
 * so the renderer can play effects, swap models and write the log without
 * re-deriving any of it.
 */

import { shipBlastPass } from './shipDamage';
import { craterEdit, moundEdit, craterDepthCap, cappedCraterDepth, floorCrater, rimCrater, digsUnder, craterletsUnder } from './terrainEdits';
import { recolorVertices, markGroundLook } from './planetColors';
import { groundMaterial, groundMaterialsOn } from './groundMaterial';
import { GROUND_VS, SAME } from '../constants/ground';
import type { GroundMaterial, GroundEffect } from '../types/ground';
import type {
  GameState, PlanetState, PlayerState, ProjectileState, StructureState, Vec3, IncomingFireIntel, WeaponKey, TerrainEdit } from '../types';
import { WEAPONS, PHYS_DT } from '../constants';
import { vecNormalize, vecSub } from '../math';
import { creditHit, friendlyShare,
  calculateDamage, createCrater, createMound, applyTerrainDropDamage, computeAntipodal,
  applyEmpEffect, applyTetherEffect, createFireZone, nextFireZoneId, type CraterResult, type TerrainDropResult, type BurstModel,
} from './damage';
import { installationsOn, shieldFactorFor } from './installations';
import { getSonoraModifiers, computeCrystalShatterDamage } from './hazardEffects';
import { applyDestructionFine } from './economy';
import { getHazardWorldPosition, addPendingCluster } from './gameState';
import { getPlayerCarrying, dropOnDeath, computeFlagStatus } from './carryables';
import { dropCollectiblesOnDeath } from './collectibles';
import { dropHealItemsOnDeath } from './groundItems';
import { addDeathCooldown } from './respawn';
import { checkFloraDestruction, recordFloraDestruction } from './floraTracking';
import { blastProps } from './propDamage';
import { clockPolicyFor } from '../match/clockPolicy';
import { structureShapeReach } from './blastReach';
import { shedStructureParts } from './partDebris';
import { SeededRandom } from './random';
import { noteHeightmapEdit } from './surface';

// =============================================================================
// Types
// =============================================================================

export type CraterFn = (planet: PlanetState, impactPoint: Vec3, blastRadius: number, depthMultiplier: number) => CraterResult;

export interface ImpactInput {
  impactPoint: Vec3;
  planetIndex: number;
  projectile: ProjectileState;
  /** Air burst (early detonation): blast only — no crater, terrain drop, specials, flora or intel. */
  airBurst?: boolean;
  /** 69.27 (DECISIONS 254 (1)): the reach the props are felled within (world units) — a crash piece's: its wreck and a blast; absent: the weapon's blast radius. */
  propReach?: number;
}

export interface ImpactDeps {
  /** Normal-crater implementation (default: JS createCrater; the game passes a WASM-backed one). */
  createCraterImpl?: CraterFn;
  /** 62.8: the struck are people of this k (the action clock's shells): their push is converted into the person family. */
  personScale?: number;
  /**
   * 69.65 (DECISIONS 289): an action shell as a person-family burst — the model (two radii for people, a hull and a
   * structure over its kill radius), its heart against hulls and structures, and its penetration against the zones. The
   * crater, the push and everything else stay the shell's.
   */
  burst?: { model: BurstModel; damage: number; pen?: number };
}

export interface DroppedIds {
  structures: number[];
  hazards: number[];
  ruins: number[];
  dropCans: number[];
  flora: number[];
  collectibles: number[];
  carryables: number[];
  groundItems: number[];
}

export interface ImpactResolution {
  state: GameState;
  /** Log lines, in the order the original code emitted them. */
  logs: string[];
  /** Blast damage per player (for combat-intensity cues). */
  damages: Array<{ playerId: number; damage: number; isBot: boolean }>;
  /** Players killed by this impact (blast only; shatter kills are counted in `launched`/logs). */
  kills: number[];
  /** Players knocked off the surface (blast + crystal shatter) — set the airborne animation. */
  launched: number[];
  /** Players whose knockback a crater rim cut (37.18 cover). */
  sheltered: number[];
  /** Terrain mesh update needed for the impacted planet. */
  terrain: { planetIndex: number; planetId: number; modifiedVertices: number[]; newColors: Float32Array; /** CraterResult's: the vertices whose colours may have changed (absent: any) */ recoloured?: number[] } | null;
  /** 62.11: the terrain edits this impact made, in order (also appended to `state.terrainEdits`). */
  edits: TerrainEdit[];
  /** Sonora resonance ripple at the impact. */
  ripple: { point: Vec3; normal: Vec3 } | null;
  /** Objects that slid down with the terrain and need their meshes repositioned. */
  dropped: DroppedIds;
  /** Structures that must show the ruined model (collapsed, blasted, or shattered). */
  ruinedStructures: number[];
  /** Hazards removed (blast first, then terrain-drop-only), with crystal shatter positions. */
  destroyedHazards: number[];
  shatterPositions: Vec3[];
  destroyedFlags: number[];
  destroyedDropCans: number[];
  /** Bot targets that were hit: where the shot came from. */
  incomingFireIntel: Array<{ playerId: number; intel: IncomingFireIntel }>;
}

// =============================================================================
// Helpers
// =============================================================================

/** 72.3: the context a building whose health is its modules' takes a terrain drop or a shatter with (any so marked: the collapse switch). */
const modularOf = (state: GameState): { config: GameState['config']; tick: number } | undefined =>
  state.config.collapse ? { config: state.config, tick: state.tick ?? state.turnNumber } : undefined;

function emptyDrop(state: GameState): TerrainDropResult {
  return {
    structures: state.structures,
    hazards: state.hazards,
    destroyedStructures: [],
    destroyedHazards: [],
    droppedStructures: [],
    droppedHazards: [],
    droppedRuinIds: [],
    plantedFlags: state.plantedFlags,
    destroyedFlags: [],
    droppedFlags: [],
    crashedDropCans: state.crashedDropCans,
    destroyedDropCans: [],
    droppedDropCans: [],
    droppedFloraIds: [],
    droppedCollectibleIds: [],
    droppedCarryableIds: [],
    droppedGroundItemIds: [],
  };
}

/**
 * Respawn-mode bookkeeping for a death (pure version of Game.handleDeathRespawnCooldown).
 * The cooldown counts turns, which only the turn clock ticks down; on the action clock a death waits
 * for the next respawn wave instead, as a bullet death always has (R1).
 */
export function applyDeathCooldown(state: GameState, killId: number): GameState {
  const ms = state.modeState;
  if (!ms.respawn) return state;
  if (state.config.clock === 'action') return state;
  if (!state.players.find(p => p.id === killId)) return state;

  const cooldown = state.config.modeConfig.respawnCooldown ?? 2;
  const respawn = addDeathCooldown(ms.respawn, killId, cooldown);
  if (ms.ctf) {
    return { ...state, modeState: { ...ms, respawn, ctf: { ...ms.ctf, flagStatus: computeFlagStatus(state.carryables) } } };
  }
  return { ...state, modeState: { ...ms, respawn } };
}

function carriedLabel(type: string): string {
  return type === 'navCore' ? 'the nav core' : type;
}

// =============================================================================
// Resolver
// =============================================================================

/** A pass's recoloured vertices added to a crater result's (none kept when its colours were computed whole). */
function withRecoloured(r: CraterResult, more: readonly number[]): Pick<CraterResult, 'recoloured'> {
  return r.recoloured ? { recoloured: [...r.recoloured, ...more] } : {};
}

export function resolveImpact(state: GameState, input: ImpactInput, deps: ImpactDeps = {}): ImpactResolution {
  const { impactPoint, planetIndex, projectile } = input;
  const reach = clockPolicyFor(state.config).blastReach;   // 63.4 step 7: how a blast reaches what stands (the clock's policy)
  const baseWeapon = WEAPONS[projectile.weaponKey];
  // The shooter's kit scales blast radius and damage (defaults ×1 — identical to the base weapon)
  const shooterMods = state.players.find(p => p.id === projectile.ownerId)?.modifiers;
  const weapon = shooterMods && (shooterMods.damageMult !== 1 || shooterMods.blastMult !== 1)
    ? { ...baseWeapon, damage: baseWeapon.damage * shooterMods.damageMult, blastRadius: baseWeapon.blastRadius * shooterMods.blastMult }
    : baseWeapon;
  const special = weapon.special;
  const airBurst = input.airBurst === true;
  const planet = state.planets[planetIndex];
  const craterImpl = deps.createCraterImpl ?? createCrater;

  const logs: string[] = [];
  const launched: number[] = [];
  const sheltered: number[] = [];
  const shatterPositions: Vec3[] = [];
  const ruinedStructures: number[] = [];
  const destroyedHazards: number[] = [];

  // ---- 1. Crater / mound --------------------------------------------------
  const sonora = airBurst ? { craterMult: 1, knockbackMult: 1 } : getSonoraModifiers(planet);
  let effectiveDamagePoint = impactPoint;
  let craterResult: CraterResult;
  // 62.11: what this impact does to the terrain, as causes (a point, a radius, a depth), in order
  const edits: TerrainEdit[] = [];
  const canEdit = !airBurst && !weapon.noCrater && !planet.indestructible;
  // decision 134: at person scale a crater keeps its radius but digs no deeper than a few person heights below the
  // generated ground — each bowl shallower, and stacked bowls floored — so craters are foxholes, never pits
  const cap = craterDepthCap(state);
  const floored = (r: CraterResult, floor: number | null = cap): CraterResult => {
    if (floor === null || r.modifiedVertices.length === 0) return r;
    const f = floorCrater(r.planet, r.modifiedVertices, floor);
    return f.lifted.length ? { planet: f.planet, modifiedVertices: r.modifiedVertices, newColors: recolorVertices(f.planet, r.newColors, f.lifted), ...withRecoloured(r, f.lifted) } : r;
  };
  // 63.5 step 2: the ground's material where a crater is dug (read before the dig, the weather of the moment), and what
  // a shell's blast does to a crater in it — step 3: GROUND_VS's blast row on the clock's ground (63.5 step 6: the turn game's plain:
  // every factor 1, as before); the edit records the crater as dug, its material and its rim
  const actionSeconds = (state.tick ?? 0) * PHYS_DT;
  const dugIn = (at: Vec3) => groundMaterial(state, planetIndex, vecNormalize(vecSub(at, planet.position)), actionSeconds);
  const materials = groundMaterialsOn(state.config);   // 63.5 step 6 (155): the clock's ground, no switch
  const factor = (m: GroundMaterial) => (materials ? GROUND_VS.blast[m] : SAME);
  // the foxhole by the ground: at person scale the cap, not the shell, sets a crater's depth (a heavy shell's bowl is far
  // deeper than a few person heights), so the ground's depth factor scales the cap too — rock's foxhole half as deep, ash's
  // deeper — in the bowl and its floor alike (the edit records it, so a replay floors the same); × 1 with the switch off
  const capIn = (f: GroundEffect) => (cap === null ? null : cap * f.depth);
  // 63.5 step 3: the rim raised round a bowl (rock's lip, salt's crust) — a pass after the dig and its floor, as replayed
  const rimmed = (r: CraterResult, at: Vec3, radius: number, depth: number, rim: number): CraterResult => {
    if (!(rim > 0) || r.modifiedVertices.length === 0) return r;
    const k = rimCrater(r.planet, at, radius, depth, rim);
    return k.raised.length ? { planet: k.planet, modifiedVertices: [...r.modifiedVertices, ...k.raised], newColors: recolorVertices(k.planet, r.newColors, k.raised), ...withRecoloured(r, k.raised) } : r;
  };
  // 63.5 step 5 (the clock's ground; step 6: the turn game's plain ground has none): each crater's look by the ground it was dug in, out to its throw —
  // marked on the planet and those vertices recoloured (presentation: the sim reads none of it)
  const looked = (r: CraterResult, craters: Array<[Vec3, number, GroundMaterial]>): CraterResult => {
    if (!materials || r.modifiedVertices.length === 0) return r;
    let p = r.planet; const all: number[] = [];
    for (const [at, rad, m] of craters) { const k = markGroundLook(p, at, rad, m); p = k.planet; all.push(...k.marked); }
    return all.length ? { planet: p, modifiedVertices: r.modifiedVertices, newColors: recolorVertices(p, r.newColors, all), ...withRecoloured(r, all) } : r;
  };

  if (airBurst || weapon.noCrater) {
    craterResult = { planet, modifiedVertices: [], newColors: new Float32Array(0) };
  } else if (special === 'terraformer') {
    craterResult = createMound(planet, impactPoint, weapon.blastRadius);
    if (canEdit) edits.push(moundEdit(planetIndex, planet, impactPoint, weapon.blastRadius));
  } else if (special === 'drill') {
    // Small entry crater at the impact, main crater (and damage) at the antipode
    effectiveDamagePoint = computeAntipodal(planet, impactPoint);
    const entryIn = dugIn(impactPoint), mainIn = dugIn(effectiveDamagePoint), fe = factor(entryIn), fm = factor(mainIn);
    const entryR = weapon.blastRadius * 0.3 * fe.radius, mainR = weapon.blastRadius * fm.radius;
    const entryCap = capIn(fe), mainCap = capIn(fm);
    const entryDepth = cappedCraterDepth(1.0 * fe.depth, entryR, entryCap), mainDepth = cappedCraterDepth(sonora.craterMult * fm.depth, mainR, mainCap);
    const entry = rimmed(floored(createCrater(planet, impactPoint, entryR, entryDepth), entryCap), impactPoint, entryR, entryDepth, fe.rim);
    const main = rimmed(floored(createCrater(entry.planet, effectiveDamagePoint, mainR, mainDepth), mainCap), effectiveDamagePoint, mainR, mainDepth, fm.rim);
    if (canEdit) edits.push(craterEdit(planetIndex, planet, impactPoint, entryR, entryDepth, entryCap, entryIn, fe.rim), craterEdit(planetIndex, planet, effectiveDamagePoint, mainR, mainDepth, mainCap, mainIn, fm.rim));
    craterResult = looked({
      planet: main.planet,
      modifiedVertices: [...entry.modifiedVertices, ...main.modifiedVertices],
      newColors: main.newColors,
    }, [[impactPoint, entryR, entryIn], [effectiveDamagePoint, mainR, mainIn]]);
  } else if (planet.indestructible) {
    craterResult = createCrater(planet, impactPoint, weapon.blastRadius, sonora.craterMult); // returns planet unchanged
  } else {
    const material = dugIn(impactPoint), f = factor(material), radius = weapon.blastRadius * f.radius;
    const floor = capIn(f), depth = cappedCraterDepth(sonora.craterMult * f.depth, radius, floor);
    craterResult = looked(rimmed(floored(craterImpl(planet, impactPoint, radius, depth), floor), impactPoint, radius, depth, f.rim), [[impactPoint, radius, material]]);
    if (canEdit) edits.push(craterEdit(planetIndex, planet, impactPoint, radius, depth, floor, material, f.rim));
  }

  const terrainChanged = !airBurst && !weapon.noCrater && craterResult.modifiedVertices.length > 0;
  const ripple = !airBurst && sonora.craterMult > 1.0 && !weapon.noCrater
    ? { point: impactPoint, normal: vecNormalize(vecSub(impactPoint, planet.position)) }
    : null;

  const newPlanets = [...state.planets];
  newPlanets[planetIndex] = craterResult.planet;
  // (62.56's follow-up: the crater's heightmap patches the sight lines' bound tables from its parent's — core/surface.ts)
  if (terrainChanged) noteHeightmapEdit(planet.heightmap, craterResult.planet.heightmap, craterResult.modifiedVertices);
  // 66.8 (§5.18): a shell ends the digs under its craters (the log keeps both, so replay agrees: applyTerrainEdit) — 69.41:
  // and the craterlets (63.11), as the replay always did: live they stayed, so a replay or a re-entry rebuilt other ground
  for (const e of edits) if (e.kind === 'crater' && e.planetIndex === planetIndex) newPlanets[planetIndex] = craterletsUnder(digsUnder(newPlanets[planetIndex], e.point, e.radius), e.point, e.radius);

  // ---- 2. Terrain drop ----------------------------------------------------
  const drop: TerrainDropResult = (airBurst || weapon.noCrater)
    ? emptyDrop(state)
    : applyTerrainDropDamage(
        state.structures, state.hazards, state.ruins, planet, craterResult.planet, planetIndex,
        state.plantedFlags, state.crashedDropCans, state.flora, state.collectibles ?? [],
        state.carryables, state.groundItems, modularOf(state),
      );

  for (const sId of drop.destroyedStructures) {
    const s = drop.structures.find(st => st.id === sId);
    if (s) {
      const owner = state.players.find(p => p.id === s.ownerId);
      if (owner) logs.push(`${owner.name}'s structure collapsed into crater!`);
      ruinedStructures.push(sId);
    }
  }
  for (let i = 0; i < drop.destroyedHazards.length; i++) logs.push('Environmental hazard collapsed into crater!');

  // ---- 3. Blast damage ----------------------------------------------------
  const shooter = state.players.find(p => p.id === projectile.ownerId);
  const knockbackMult = (airBurst ? 1.0 : (weapon.knockbackForce ?? sonora.knockbackMult)) * (state.config.knockbackMultiplier ?? 1.0);
  const dmg = calculateDamage(
    state.players, newPlanets, effectiveDamagePoint, projectile.weaponKey,
    drop.structures, shooter?.teamIndex, friendlyShare(state.config),
    drop.hazards, knockbackMult, projectile.ownerId, drop.plantedFlags, drop.crashedDropCans,
    deps.burst ? { damage: deps.burst.damage, blastRadius: weapon.blastRadius } : weapon === baseWeapon ? undefined : { damage: weapon.damage, blastRadius: weapon.blastRadius },
    deps.personScale ?? 1,
    // 63.4 step 7: under the clock policy's `blastReach: 'shape'` a structure is reached by its shape (a shell: no cover)
    reach === 'shape' ? structureShapeReach({ ...state, planets: newPlanets, structures: drop.structures }, effectiveDamagePoint, 'shell') : undefined,
    installationsOn(state) ? p => shieldFactorFor(state, p) : undefined,   // X18 step 2
    'blast', undefined, undefined,
    deps.burst?.pen !== undefined ? { pen: deps.burst.pen, at: effectiveDamagePoint, state: { ...state, planets: newPlanets } } : undefined,   // 69.65: its class's penetration against the zones
    deps.burst?.model,
  );

  const damages = dmg.damages.map(d => {
    const p = state.players.find(pl => pl.id === d.playerId);
    if (p) logs.push(`${p.name} takes ${Math.round(d.damage)} dmg`);
    return { playerId: d.playerId, damage: d.damage, isBot: !!p?.isBot };
  });
  for (const killId of dmg.kills) {
    const p = state.players.find(pl => pl.id === killId);
    if (p) logs.push(`${p.name} destroyed!`);
  }

  // ---- 4. Deaths: drop what they carried, start respawn cooldown ----------
  // (These touch carryables/collectibles/groundItems/modeState only; players
  // are replaced below from the damage result, exactly as before.)
  let working: GameState = state;
  if (edits.length) working = { ...working, terrainEdits: [...(state.terrainEdits ?? []), ...edits] };   // 62.11: the log
  for (const killId of dmg.kills) {
    const carried = getPlayerCarrying(killId, working.carryables);
    const killed = working.players.find(p => p.id === killId);
    working = dropOnDeath(working, killId);
    working = dropCollectiblesOnDeath(working, killId);
    working = dropHealItemsOnDeath(working, killId);
    if (carried && killed) logs.push(`${killed.name} dropped ${carriedLabel(carried.type)}!`);
    working = applyDeathCooldown(working, killId);
  }

  // ---- 5. Knockbacks ------------------------------------------------------
  for (const kb of dmg.knockbacks) {
    const p = state.players.find(pl => pl.id === kb.playerId);
    if (!p) continue;
    if (kb.sheltered) { logs.push(`${p.name} sheltered by the rim`); sheltered.push(kb.playerId); }
    if (kb.launched) { logs.push(`${p.name} launched into orbit!`); launched.push(kb.playerId); }
    else logs.push(`${p.name} knocked back!`);
  }

  // ---- 6. Incoming-fire intel for damaged bots ----------------------------
  const incomingFireIntel: ImpactResolution['incomingFireIntel'] = [];
  if (!airBurst) {
    for (const d of dmg.damages) {
      const p = state.players.find(pl => pl.id === d.playerId);
      if (p && p.isBot && p.id !== projectile.ownerId) {
        incomingFireIntel.push({
          playerId: d.playerId,
          intel: {
            impactPos: impactPoint,
            impactVel: { x: projectile.vel.x, y: projectile.vel.y, z: projectile.vel.z },
            impactTurn: state.turnNumber,
            shooterId: projectile.ownerId,
          },
        });
      }
    }
  }

  // ---- 7. Shooter stats ---------------------------------------------------
  const totalDamage = dmg.damages.reduce((s, d) => s + d.damage, 0);
  let players: PlayerState[] = dmg.players.map(p =>
    p.id === projectile.ownerId ? creditHit(p, totalDamage, dmg.kills.length) : p,
  );

  // ---- 8. Structures destroyed by the blast (+ fine) ----------------------
  let structures: StructureState[] = dmg.structures;
  for (const sId of dmg.destroyedStructures) {
    const destroyed = state.structures.find(s => s.id === sId);
    if (!destroyed) continue;
    const owner = players.find(p => p.id === destroyed.ownerId);
    if (owner) {
      logs.push(`${owner.name}'s ecotourism center destroyed! (-2 budget)`);
      const newCbp = applyDestructionFine(owner);
      players = players.map(p => (p.id === owner.id ? { ...p, cbp: newCbp } : p));
    }
    ruinedStructures.push(sId);
  }

  // ---- 9. Hazards destroyed (blast, then terrain-drop-only) + crystal shatter
  const applyShatter = (hazardId: number) => {
    const hz = state.hazards.find(h => h.id === hazardId);
    if (!hz || hz.hazardType !== 'crystal') return;
    const shatter = computeCrystalShatterDamage(
      hz, players, newPlanets, structures, shooter?.teamIndex, friendlyShare(state.config), modularOf(state),
    );
    players = shatter.players;
    structures = shatter.structures;
    for (const d of shatter.damages) {
      const p = players.find(pl => pl.id === d.playerId);
      if (p) logs.push(`${p.name} takes ${Math.round(d.damage)} crystal shrapnel dmg`);
    }
    for (const kb of shatter.knockbacks) {
      const p = players.find(pl => pl.id === kb.playerId);
      if (p && kb.launched) { logs.push(`${p.name} launched by crystal shatter!`); launched.push(kb.playerId); }
    }
    shatterPositions.push(getHazardWorldPosition(hz, newPlanets));
    for (const sId of shatter.destroyedStructures) {
      const destroyed = state.structures.find(s => s.id === sId);
      if (destroyed) {
        const owner = players.find(p => p.id === destroyed.ownerId);
        if (owner) logs.push(`${owner.name}'s ecotourism center destroyed by crystal shatter!`);
        ruinedStructures.push(sId);
      }
    }
  };

  for (const hId of dmg.destroyedHazards) {
    logs.push('Environmental hazard destroyed!');
    applyShatter(hId);
    destroyedHazards.push(hId);
  }
  const blastDestroyed = new Set(dmg.destroyedHazards);
  for (const hId of drop.destroyedHazards) {
    if (blastDestroyed.has(hId)) continue;
    applyShatter(hId);
    destroyedHazards.push(hId);
  }

  // ---- 10. Flags and drop cans ---------------------------------------------
  const destroyedFlags = [...new Set([...dmg.destroyedFlags, ...drop.destroyedFlags])];
  for (let i = 0; i < destroyedFlags.length; i++) logs.push('A stake was destroyed!');
  const destroyedDropCans = [...new Set([...dmg.destroyedDropCans, ...drop.destroyedDropCans])];

  // ---- 11. Commit -----------------------------------------------------------
  working = {
    ...working,
    planets: newPlanets,
    players,
    structures,
    hazards: dmg.hazards,
    plantedFlags: dmg.plantedFlags,
    crashedDropCans: dmg.crashedDropCans,
  };

  // ---- 12. Special weapon effects --------------------------------------------
  if (!airBurst) {
    // EMP / tether targets are chosen from where players stood BEFORE the blast.
    // (Previously they were evaluated after knockback, and since every player
    // inside the EMP radius is also inside the launch radius, grounded targets
    // were airborne by then and never got disrupted.)
    if (special === 'emp') {
      const duration = weapon.empDuration ?? 2;
      const probe = applyEmpEffect(state.players, newPlanets, effectiveDamagePoint, weapon.blastRadius, duration);
      const hit = new Set(probe.filter((p, i) => p.empTurnsRemaining && !state.players[i].empTurnsRemaining).map(p => p.id));
      working = { ...working, players: working.players.map(p => (hit.has(p.id) && p.alive ? { ...p, empTurnsRemaining: duration } : p)) };
      for (const p of working.players) if (hit.has(p.id) && p.alive) logs.push(`${p.name}'s systems disrupted by EMP!`);
    }
    if (special === 'tether') {
      const probe = applyTetherEffect(state.players, newPlanets, effectiveDamagePoint, weapon.blastRadius);
      const hit = new Set(probe.filter(p => p.tetheredTurns && p.tetheredTurns > 0).map(p => p.id));
      working = { ...working, players: working.players.map(p => (hit.has(p.id) && p.alive ? { ...p, tetheredTurns: 1, movementRemaining: 0 } : p)) };
      for (const p of working.players) if (p.tetheredTurns && p.tetheredTurns > 0) logs.push(`${p.name} tethered in place!`);
    }
    if (special === 'fire') {
      const zone = createFireZone(effectiveDamagePoint, planetIndex, weapon.blastRadius, weapon.fireDuration ?? 2, projectile.ownerId, nextFireZoneId(working.fireZones));
      working = { ...working, fireZones: [...(working.fireZones ?? []), zone] };
      logs.push('Fire zone created!');
    }

    // ---- 13. Flora destruction ------------------------------------------------
    if (reach === 'shape') {
      // 63.4 step 6 (130 (a)): the trees within the shell's own radius take it through the damage door (a shell fells one
      // in a hit; constants/damage.ts), logged; a mode that keeps flora's score counts them. The turn game keeps its flora
      // rule (below): the clock policy's `blastReach` says which — a policy of the clock, not of scale
      const props = blastProps(working, planetIndex, impactPoint, input.propReach ?? weapon.blastRadius, weapon.damage, 'blast', projectile.ownerId);   // (69.27: a crash's own reach)
      working = props.state;
      if (props.felled.length && working.floraDestruction) {
        const team = working.players.find(p => p.id === projectile.ownerId)?.teamIndex ?? 0;
        let fd = working.floraDestruction;
        for (const id of props.felled) { const f = working.flora.find(fl => fl.id === id); if (f) fd = recordFloraDestruction(fd, f.planetIndex, projectile.ownerId, team); }
        working = { ...working, floraDestruction: fd };
      }
    } else if (working.floraDestruction && working.flora && working.flora.length > 0) {
      const { destroyedIds, updatedFlora } = checkFloraDestruction(working.flora, impactPoint, input.propReach ?? weapon.blastRadius, working.planets);
      if (destroyedIds.length > 0) {
        const shooterPlayer = working.players.find(p => p.id === projectile.ownerId);
        let fd = working.floraDestruction;
        for (const floraId of destroyedIds) {
          const f = working.flora.find(fl => fl.id === floraId);
          if (f) fd = recordFloraDestruction(fd, f.planetIndex, projectile.ownerId, shooterPlayer?.teamIndex ?? 0);
        }
        working = { ...working, flora: updatedFlora, floraDestruction: fd };
      }
    }
  }

  // partial destruction step 5: a detaching part this blast took to 0 falls away as debris (core/partDebris.ts)
  if (reach === 'shape') working = shedStructureParts(state.structures, working, effectiveDamagePoint, 'blast', projectile.ownerId);
  // 69.3b, the ship as a body: the ships within the shell's reach wear it (nothing with the switch off; an air burst reaches none)
  if (planetIndex >= 0) working = shipBlastPass(working, planetIndex, effectiveDamagePoint, weapon.blastRadius, weapon.damage, 'blast');

  return {
    state: working,
    logs,
    damages,
    kills: dmg.kills,
    launched,
    sheltered,
    terrain: terrainChanged
      ? { planetIndex, planetId: planet.id, modifiedVertices: craterResult.modifiedVertices, newColors: craterResult.newColors, ...(craterResult.recoloured ? { recoloured: craterResult.recoloured } : {}) }
      : null,
    edits,
    ripple,
    dropped: {
      structures: drop.droppedStructures,
      hazards: drop.droppedHazards,
      ruins: drop.droppedRuinIds,
      dropCans: drop.droppedDropCans,
      flora: drop.droppedFloraIds,
      collectibles: drop.droppedCollectibleIds,
      carryables: drop.droppedCarryableIds,
      groundItems: drop.droppedGroundItemIds,
    },
    ruinedStructures: [...new Set(ruinedStructures)],
    destroyedHazards,
    shatterPositions,
    destroyedFlags,
    destroyedDropCans,
    incomingFireIntel,
  };
}

// =============================================================================
// Cluster munitions
// =============================================================================

/**
 * Queue the submunitions of a cluster weapon that just impacted. Spread is
 * seeded from match seed + turn + owner + impact point so both P2P peers
 * spawn identical bomblets. No-op if the weapon has no cluster or
 * submunitions are already pending (a bomblet landing must not re-spawn).
 */
export function queueClusterSubmunitions(state: GameState, weaponKey: WeaponKey, impactPoint: Vec3, planetIndex: number, ownerId: number): GameState {
  const weapon = WEAPONS[weaponKey];
  if (!weapon.cluster || state.pendingClusters.length > 0) return state;
  const planet = state.planets[planetIndex];
  const normal = vecNormalize(vecSub(impactPoint, planet.position));

  const rng = new SeededRandom(
    `${state.config.seed}-cluster-${state.turnNumber}-${ownerId}-`
    + `${Math.round(impactPoint.x * 100)},${Math.round(impactPoint.y * 100)},${Math.round(impactPoint.z * 100)}`,
  );
  let s = state;
  for (let i = 0; i < weapon.cluster.count; i++) {
    const d = { x: (rng.next() - 0.5) * 2, y: (rng.next() - 0.5) * 2, z: (rng.next() - 0.5) * 2 };
    const len = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z);
    const vel = {
      x: (d.x / len) * weapon.cluster.spread * 12 + normal.x * 7,
      y: (d.y / len) * weapon.cluster.spread * 12 + normal.y * 7,
      z: (d.z / len) * weapon.cluster.spread * 12 + normal.z * 7,
    };
    const pos = { x: impactPoint.x + normal.x, y: impactPoint.y + normal.y, z: impactPoint.z + normal.z };
    s = addPendingCluster(s, pos, vel, weapon.cluster.subWeapon);
  }
  return s;
}
